import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import NewLogoHeader from '../../assets/NewLogoHeader';
import { ChevronDownIcon } from '../../assets/ChevronDownIcon';
import { MenuIcon } from '../../assets/MenuIcon';
import { cn } from '../../lib/utils';
import {
	PUBLIC_HEADER_LINKS,
	PUBLIC_HEADER_MOBILE_LINKS,
	PUBLIC_HEADER_MORE_LINKS,
} from './publicHeader.constants';
import { landingUrl } from './publicHeader.utils';
import PublicLanguagePicker from './PublicLanguagePicker';
import './public-header.css';

const PublicHeader: React.FC = () => {
	const { t } = useTranslate();
	const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);
	const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);
	const closeTimerRef = useRef<number | null>(null);

	const clearCloseTimer = () => {
		if (closeTimerRef.current !== null) {
			window.clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	};

	const handleMoreEnter = () => {
		clearCloseTimer();
		setIsMoreOpen(true);
	};

	const handleMoreLeave = () => {
		clearCloseTimer();
		closeTimerRef.current = window.setTimeout(() => {
			setIsMoreOpen(false);
			closeTimerRef.current = null;
		}, 200);
	};

	useEffect(() => clearCloseTimer, []);

	return (
		<header className="absolute top-0 left-0 right-0 z-[1100] flex flex-col items-center">
			<div
				className={cn(
					'hidden lg:flex w-full justify-center transition-all duration-300',
					'bg-white/40 backdrop-blur-lg border-0 border-b border-solid border-white/50 shadow-[0_4px_24px_rgba(0,0,0,0.08)]',
				)}
			>
				<div className="w-full h-[64px] flex justify-between items-center gap-8 text-sm px-7 box-border">
					<a href={landingUrl('/')} aria-label="SYPAC" className="flex">
						<NewLogoHeader />
					</a>

					<div className="flex items-center gap-10">
						<nav className="flex items-center gap-8">
							{PUBLIC_HEADER_LINKS.map((link) => (
								<a
									key={link.path}
									href={landingUrl(link.path)}
									className="public-header-link no-underline"
								>
									{t(link.labelKey, link.defaultLabel)}
								</a>
							))}
							<div
								className="relative inline-block"
								onMouseEnter={handleMoreEnter}
								onMouseLeave={handleMoreLeave}
							>
								<button
									type="button"
									className="group flex items-center gap-2 border-none bg-transparent p-0 font-[inherit] text-sm text-gray-80 cursor-pointer"
									onClick={() => setIsMoreOpen((value) => !value)}
								>
									<span>{t('publicHeader.more', 'More')}</span>
									<span
										className={cn(
											'flex -rotate-90 transition-transform duration-300 group-hover:rotate-0',
											isMoreOpen && 'rotate-0',
										)}
									>
										<ChevronDownIcon color="#747474" width={10} height={6} />
									</span>
								</button>

								{isMoreOpen ? (
									<div className="absolute right-0 top-[28px] w-64 bg-white rounded-lg shadow-dropdown z-50">
										<ul className="m-0 p-1 list-none">
											{PUBLIC_HEADER_MORE_LINKS.map((link) => (
												<li
													key={link.path}
													className="bg-white rounded-[5px] transition hover:bg-gray-10"
												>
													<a
														href={landingUrl(link.path)}
														className="block px-7 py-[15px] no-underline font-medium text-gray-80"
													>
														{t(link.labelKey, link.defaultLabel)}
													</a>
												</li>
											))}
										</ul>
									</div>
								) : null}
							</div>
						</nav>

						<PublicLanguagePicker />

						<div className="flex gap-3 items-center">
							<a
								href={landingUrl('/register')}
								className="w-[122px] h-8 flex items-center justify-center bg-primary-violet rounded-md no-underline text-sm text-white transition hover:bg-primary-violet/90"
							>
								{t('publicHeader.register', 'Register')}
							</a>
							<a
								href={landingUrl('/login')}
								className="w-[122px] h-8 flex items-center justify-center bg-white rounded-md border border-solid border-gray-20 box-border no-underline text-sm text-primary-violet transition hover:border-primary-violet"
							>
								{t('publicHeader.login', 'Login')}
							</a>
						</div>
					</div>
				</div>
			</div>

			<div
				className={cn(
					'lg:hidden w-full h-[62px] flex justify-between items-center px-3 box-border',
					'bg-white/40 backdrop-blur-lg border-0 border-b border-solid border-white/50 shadow-[0_4px_24px_rgba(0,0,0,0.08)]',
				)}
			>
				<a href={landingUrl('/')} aria-label="SYPAC" className="flex">
					<NewLogoHeader width={116} height={21} />
				</a>
				<button
					type="button"
					onClick={() => setIsMobileOpen((value) => !value)}
					className="w-[87px] h-[42px] flex items-center justify-center gap-[6px] bg-white rounded-10 border border-solid border-gray-10 cursor-pointer"
				>
					<span className="flex">
						<MenuIcon />
					</span>
					<span className="text-sm text-gray-80">
						{isMobileOpen
							? t('publicHeader.back', 'Back')
							: t('publicHeader.menu', 'Menu')}
					</span>
				</button>
			</div>

			{isMobileOpen ? (
				<div className="lg:hidden fixed top-[62px] left-0 right-0 bottom-0 bg-alabaster overflow-y-auto">
					<ul className="list-none m-3 p-0 flex flex-col gap-3">
						<li>
							<PublicLanguagePicker variant="mobile" />
						</li>
						<li>
							<a
								href={landingUrl('/register')}
								className="flex items-center h-[60px] px-5 rounded-xl bg-primary-violet text-white no-underline text-base"
							>
								{t('publicHeader.register', 'Register')}
							</a>
						</li>
						<li>
							<a
								href={landingUrl('/login')}
								className="flex items-center h-[60px] px-5 rounded-xl bg-white border border-solid border-gray-10 text-primary-violet no-underline text-base"
							>
								{t('publicHeader.login', 'Login')}
							</a>
						</li>
						{PUBLIC_HEADER_MOBILE_LINKS.map((link) => (
							<li key={link.path}>
								<a
									href={landingUrl(link.path)}
									className="flex items-center h-[60px] px-5 rounded-xl bg-white border border-solid border-gray-10 text-gray-80 no-underline"
								>
									{t(link.labelKey, link.defaultLabel)}
								</a>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</header>
	);
};

export default PublicHeader;
